import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import { ReactNode, useEffect, useRef, useState } from "react";

export const DropdownNew = ({
  children,
  menuItems,
  customMenuItems = undefined,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  closeOnClick = true,
  menuExactWidth = false,
  disabled = false,
}: {
  children: ReactNode;
  menuItems?: {
    name: ReactNode;
    value?: string;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }[];
  customMenuItems?:
    | ReactNode
    | ((props: { open: boolean; close: () => void }) => ReactNode);
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  position?: AnchorProps;
  closeOnClick?: boolean;
  menuExactWidth?: boolean;
  disabled?: boolean;
}) => {
  const [width, setWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  return (
    <Menu
      as="div"
      className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
      data-open={true}
    >
      {({ open, close }) => {
        return (
          <>
            <div
              className="w-full outline-none focus-within:outline-none focus:outline-none"
              ref={containerRef}
            >
              <MenuButton as={"div"} autoFocus={false} disabled={disabled}>
                {children}
              </MenuButton>
            </div>

            <MenuItems
              className={classNames(
                `relative z-[900] flex flex-col overflow-x-hidden overflow-y-scroll`,
                {
                  "rounded-md border bg-white": customMenuItems === undefined,
                  "w-full min-w-max": !menuExactWidth,
                },
                "[--anchor-gap:16px]",
                "outline-none focus-within:outline-none focus:outline-none",
                menuClassName
              )}
              transition
              anchor={position}
              style={{
                width: width,
                minWidth: menuExactWidth ? `${width} !important` : undefined,
                maxWidth: menuExactWidth ? `${width} !important` : undefined,
              }}
              portal
            >
              {customMenuItems !== undefined ? (
                <MenuItem
                  as={"div"}
                  onClick={(e) => {
                    if (!closeOnClick) {
                      e.preventDefault();
                    }
                  }}
                >
                  {typeof customMenuItems === "function"
                    ? customMenuItems({ open, close })
                    : customMenuItems}
                </MenuItem>
              ) : (
                menuItems?.map((item, index) => {
                  const key = `menu-item-${index}`;
                  return (
                    <MenuItem
                      key={key}
                      as="button"
                      type="button"
                      onClick={(e) => {
                        if (!closeOnClick) {
                          e.preventDefault();
                        }
                        if (item.onClick) {
                          item.onClick(e);
                        }
                      }}
                      className={classNames(
                        "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                        menuItemsClassName
                      )}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })
              )}
            </MenuItems>
          </>
        );
      }}
    </Menu>
  );
};
